/**
 * @fileoverview Experiment dimensions for analytics fan-out.
 * @description Builds flat properties attached to analytics events. Supports
 * multi-experiment sessions with indexed keys for GA4 explorations.
 *
 * @module lib/experiments/dimensions
 */

import type { EventPropValue } from "~/lib/analytics/registry"

import { isSurfaceMatch, normalizePathname } from "./surfaces"
import type { ExperimentKind, ExperimentMetric, SurfaceScope } from "./types"

/**
 * Active experiment assignment surfaced to analytics.
 */
export interface ExperimentAssignment {
  experiment_id: string
  variant_id: string
  kind: ExperimentKind
  surface: SurfaceScope
  /** Goal metric for conversion ledger fan-out. */
  primary_metric?: ExperimentMetric
}

/**
 * Filters assignments to those eligible on the current pathname.
 *
 * @param {readonly ExperimentAssignment[]} assignments - All assignments.
 * @param {string} pathname - Current page path.
 * @returns {readonly ExperimentAssignment[]} Surface-eligible assignments.
 */
export function filterAssignmentsForPath(
  assignments: readonly ExperimentAssignment[],
  pathname: string,
): readonly ExperimentAssignment[] {
  const path = normalizePathname(pathname)
  return assignments.filter((a) => isSurfaceMatch(a.surface, path))
}

/**
 * Serializes eligible experiment assignments into analytics-safe flat properties.
 *
 * @param {readonly ExperimentAssignment[]} assignments - Active assignments.
 * @param {string} [pathname] - When set, only includes surface-eligible experiments.
 * @returns {Record<string, EventPropValue>} Properties to merge into events.
 */
export function experimentDimensions(
  assignments: readonly ExperimentAssignment[],
  pathname?: string,
): Record<string, EventPropValue> {
  const eligible =
    pathname !== undefined
      ? filterAssignmentsForPath(assignments, pathname)
      : assignments

  if (eligible.length === 0) {
    return {}
  }

  const primary = eligible[0]
  if (!primary) {
    return {}
  }

  const out: Record<string, EventPropValue> = {
    experiment_id: primary.experiment_id,
    variant_id: primary.variant_id,
    experiment_count: eligible.length,
    experiment_kind: primary.kind,
  }

  eligible.forEach((assignment, index) => {
    const n = index + 1
    out[`exp_${n}_id`] = assignment.experiment_id
    out[`exp_${n}_variant`] = assignment.variant_id
    out[`exp_${n}_kind`] = assignment.kind
  })

  if (eligible.length > 1) {
    out.experiment_ids = eligible.map((a) => a.experiment_id).join(",")
    out.variant_ids = eligible.map((a) => a.variant_id).join(",")
  }

  return out
}
